import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TrainingService } from '../../../services/training.service';
import { LoadingService } from '../../../services/loading.service';
import { StorageKey, StorageService } from '../../../services/storage.service';
import { FirstStartDatePipe } from '../../../pipes/first-start-date.pipe';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { TableColumn } from 'src/app/models/table-column.model';
import { TrainingLocationTypeModel } from 'src/app/models/training/training-location-type.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { DatePipe } from '@angular/common';
import { TrainingTemplateService } from 'src/app/services/training-template.service';
import { TrainingTemplateSummaryModel } from 'src/app/models/training-template/training-template-summary.model';
import { TranslateService } from '@ngx-translate/core';
import { TrainingManageSummaryModel } from 'src/app/models/training/training-manage-summary.model';
import { forkJoin, Observable } from 'rxjs';
import { UserContextService } from 'src/app/services/user-context.service';
import { TrainingOverviewTab } from 'src/app/models/training/training-overview.tab';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-training-overview',
  templateUrl: './training-overview.component.html',
  styleUrls: ['./training-overview.component.scss'],
  standalone: false
})
export class TrainingOverviewComponent implements OnInit {
  tabsTrainingsCount: { [key: string]: number } = {};

  tabs = [
    {
      key: TrainingOverviewTab.Current,
      text: this.translateService.instant(
        'TRAINING.OVERVIEW.CURRENT-TRAININGS-TAB'
      ),
      textOrig: this.translateService.instant(
        'TRAINING.OVERVIEW.CURRENT-TRAININGS-TAB'
      ),
      loaded: false
    },
    {
      key: TrainingOverviewTab.ToBeClosed,
      text: this.translateService.instant(
        'TRAINING.OVERVIEW.TO-BE-CLOSED-TRAININGS-TAB'
      ),
      textOrig: this.translateService.instant(
        'TRAINING.OVERVIEW.TO-BE-CLOSED-TRAININGS-TAB'
      ),
      loaded: false
    },
    {
      key: TrainingOverviewTab.Closed,
      text: this.translateService.instant(
        'TRAINING.OVERVIEW.CLOSED-TRAININGS-TAB'
      ),
      textOrig: this.translateService.instant(
        'TRAINING.OVERVIEW.CLOSED-TRAININGS-TAB'
      ),
      loaded: false
    },
    {
      key: TrainingOverviewTab.Templates,
      text: this.translateService.instant('TRAINING.OVERVIEW.TEMPLATES'),
      textOrig: this.translateService.instant('TRAINING.OVERVIEW.TEMPLATES'),
      loaded: false
    },
  ];

  trainingColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DATE'),
      dataField: 'dates[0].start',
      dataType: 'date',
      cellValue: (data: any): any => {
        if (data && data.dates && data.dates[0] && data.dates[0].start) {
          return new Date(data.dates[0].start)
        } else {
          return 0;
        }
      },
      calculateDisplayValue: (data: TrainingManageSummaryModel): string => {
        const firstStartDate = new FirstStartDatePipe().transform(data.dates);
        if (!firstStartDate) {
          return '';
        } else {
          return (
            new DatePipe(this.translateService.currentLang).transform(
              firstStartDate,
              'EEEE, dd MMMM yyyy HH:mm a'
            ) || ''
          );
        }
      },
      calculateSortValue: (data: TrainingManageSummaryModel): Date | number => {
        if (data.dates && data.dates[0] && data.dates[0].start) {
          return new Date(data.dates[0].start);
        } else {
          return 0;
        }
      }
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
      dataField: 'location.location',
      cssClass: 'width-200',
      cellValue: (data: TrainingManageSummaryModel): string => {
        switch (data.location.type) {
          case TrainingLocationTypeModel.GoToWebinar:
            return 'GoTo Webinar';
          case TrainingLocationTypeModel.GotoMeeting:
            return 'GoTo Meeting';
          case TrainingLocationTypeModel.Webex:
            return 'Webex';
          case TrainingLocationTypeModel.Web:
            return 'Web';
          case TrainingLocationTypeModel.MsTeamsWebinar:
            return 'Teams Webinar';
          case TrainingLocationTypeModel.MsTeamsMeetings:
            return 'Teams Meeting';
          case TrainingLocationTypeModel.Zoom:
            return 'Zoom Webinar';

          case TrainingLocationTypeModel.ExternalEvent:
            return this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.EXTERNAL-EVENT'
            );

          default:
            return data.location.location;
        }
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.PARTICIPANT-COUNT'),
      dataField: 'participantCount',
      cellValue: (data: TrainingManageSummaryModel): string | number => {
        switch (this.selectedTab) {
          case TrainingOverviewTab.Current:
          case TrainingOverviewTab.ToBeClosed: {
            const base = data.acceptedParticipants.length;

            const additional = data.acceptedParticipants.reduce(
              (sum, member) => sum + (member.additionalParticipants?.length ?? 0),
              0
            );

            return base + additional;
          }
          case TrainingOverviewTab.Closed:
            return data.completedPersons.length;
          default:
            return 'n.A.';
        }
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TYPE'),
      dataField: 'trainingType',
      cellValue: (data: TrainingManageSummaryModel): string => {
        switch (data.trainingType) {
          case TrainingTypeModel.General:
            return this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
            );
          case TrainingTypeModel.IDD:
            return this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.IDD'
            );
          case TrainingTypeModel.Compliance:
            return this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
            );
          default:
            return 'n.A.';
        }
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-GROUPS'),
      dataField: 'trainingGroups',
      cssClass: 'width-200',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const trainingGroupNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedTrainingGroupNames: string[] = [];
          trainingGroupNames.forEach((group: string) => {
            if (!group || !group.includes(', ')) {
              parsedTrainingGroupNames.push(group);
            } else if (group.includes(', ')) {
              const groups = group.split(', ');
              parsedTrainingGroupNames.push(...groups);
            }
          });
          const uniqueTrainingGroupNames = [
            ...new Set(parsedTrainingGroupNames),
          ];
          results.splice(0, results.length);
          uniqueTrainingGroupNames.forEach((tgName) => {
            if (tgName) {
              results.push({
                text: tgName,
                value: tgName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getTrainingGroupsFilterString(rowData: TrainingManageSummaryModel) {
          const groups = rowData.trainingGroups.map((tg) => tg.name);
          return !!groups.join(',') ? groups.join(',') + ',' : 'empty';
        }

        return [[getTrainingGroupsFilterString, 'contains', value ? (value + ',') : 'empty']];
      },
      cellValue: (data: any): string => {
        return data.trainingGroups.map((tg: any) => tg.name).join(', ');
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EVENT-TYPE'),
      dataField: 'location.type',
      cellValue: (data: TrainingManageSummaryModel): string => {
        switch (data.location.type) {
          case TrainingLocationTypeModel.Address:
            return this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.FACE-TO-FACE'
            );
          case TrainingLocationTypeModel.GoToWebinar:
            return 'GoTo Webinar';
          case TrainingLocationTypeModel.GotoMeeting:
            return 'GoTo Meeting';
          case TrainingLocationTypeModel.Webex:
            return 'Webex';
          case TrainingLocationTypeModel.Web:
            return 'Web';
          case TrainingLocationTypeModel.MsTeamsMeetings:
            return 'Teams Meeting';
          case TrainingLocationTypeModel.MsTeamsWebinar:
            return 'Teams Webinar';
          case TrainingLocationTypeModel.Zoom:
            return 'Zoom Webinar';

          case TrainingLocationTypeModel.ExternalEvent:
            return this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.EXTERNAL-EVENT'
            );

          default:
            return 'n.A.';
        }
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (training: TrainingManageSummaryModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            this.trainingNavigateHandlerNewTab(training.id);
          },
        },
      ],
    },
  ];

  templatesColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-GROUPS'),
      dataField: 'trainingGroups',
      cssClass: 'width-250',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const trainingGroupNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedTrainingGroupNames: string[] = [];
          trainingGroupNames.forEach((group: string) => {
            if (!group || !group.includes(', ')) {
              parsedTrainingGroupNames.push(group);
            } else if (group.includes(', ')) {
              const groups = group.split(', ');
              parsedTrainingGroupNames.push(...groups);
            }
          });
          const uniqueTrainingGroupNames = [
            ...new Set(parsedTrainingGroupNames),
          ];
          results.splice(0, results.length);
          uniqueTrainingGroupNames.forEach((tgName) => {
            if (tgName) {
              results.push({
                text: tgName,
                value: tgName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getTrainingGroupsFilterString(
          rowData: TrainingTemplateSummaryModel
        ) {
          const groups = rowData.trainingGroups.map((tg) => tg.name);
          return !!groups.join(',') ? groups.join(',') : 'empty';
        }

        return [[getTrainingGroupsFilterString, 'contains', value || 'empty']];
      },
      cellValue: (data: any): string => {
        return data.trainingGroups.map((tg: any) => tg.name).join(', ');
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.LOCATIONS'),
      dataField: 'organisationUnitLocations',
      cssClass: 'width-250',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const trainingGroupNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedTrainingGroupNames: string[] = [];
          trainingGroupNames.forEach((group: string) => {
            if (!group || !group.includes(', ')) {
              parsedTrainingGroupNames.push(group);
            } else if (group.includes(', ')) {
              const groups = group.split(', ');
              parsedTrainingGroupNames.push(...groups);
            }
          });
          const uniqueTrainingGroupNames = [
            ...new Set(parsedTrainingGroupNames),
          ];
          results.splice(0, results.length);
          uniqueTrainingGroupNames.forEach((tgName) => {
            if (tgName) {
              results.push({
                text: tgName,
                value: tgName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getOrganisationUnitLocationsFilterString(
          rowData: TrainingTemplateSummaryModel
        ) {
          const groups = rowData.organisationUnitLocations.map((tg) => tg.name);
          return !!groups.join(',') ? groups.join(',') : 'empty';
        }

        return [
          [
            getOrganisationUnitLocationsFilterString,
            'contains',
            value || 'empty',
          ],
        ];
      },
      cellValue: (data: any): string => {
        return data.organisationUnitLocations
          .map((tg: any) => tg.name)
          .join(', ');
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (trainingTemplate: TrainingTemplateSummaryModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            this.trainingTemplateNavigateHandlerNewTab(trainingTemplate.id);
          },
        },
      ],
    },
  ];

  yearFilter: number | null = null;
  yearsDropdownDataSource: any[] = [
    {
      id: new Date().getFullYear(),
      name: new Date().getFullYear(),
    },
    {
      id: new Date().getFullYear() - 1,
      name: new Date().getFullYear() - 1,
    },
    {
      id: new Date().getFullYear() - 2,
      name: new Date().getFullYear() - 2,
    },
    {
      id: new Date().getFullYear() - 3,
      name: new Date().getFullYear() - 3,
    },
    {
      id: new Date().getFullYear() - 4,
      name: new Date().getFullYear() - 4,
    },
  ];

  currentUserRole: string|null = null;
  dataLoaded: boolean = false;
  selectedTab: TrainingOverviewTab = TrainingOverviewTab.Current;
  trainings: any[] = [];
  currentTrainings: any[] = [];
  toBeClosedTrainings: any[] = [];
  closedTrainings: any[] = [];
  trainingTemplates: TrainingTemplateSummaryModel[] = [];
  
  readonly trainingPageStateKey = 'trainings-overview-page';

  readonly trainingTableStateKey = 'trainings-overview-table';
  readonly templateTableStateKey = 'training-templates-overview-table';

  get isTemplateMode() {
    return this.selectedTab === TrainingOverviewTab.Templates;
  }

  get isClosedTab() {
    return this.selectedTab === TrainingOverviewTab.Closed;
  }

  get isTrainer(): boolean {
    return this.currentUserRole === 'Trainer';
  }

  get selectedTabIndex(): number {
    return this.tabs.findIndex(t => t.key === this.selectedTab);
  }

  constructor(
    private router: Router,
    private trainingService: TrainingService,
    private loadingService: LoadingService,
    private storageService: StorageService,
    private trainingTemplateService: TrainingTemplateService,
    private translateService: TranslateService,
    private userContext: UserContextService,
    private pageStateService: PageStateService
  ) {
  }

  ngOnInit(): void {
    this.currentUserRole = this.userContext.currentRole;
    this.yearFilter = this.yearsDropdownDataSource[0].id;

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTrainingsOverviewTable
    )) {
      this.pageStateService.clear(this.trainingTableStateKey);
    }

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTrainingTemplatesOverview
    )) {
      this.pageStateService.clear(this.templateTableStateKey);
    }


    this.configureTabsByRole();
    this.loadPageState();

    const savedTab = this.storageService.getNumber(
      StorageKey.TRAINING_OVERVIEW_TAB_INDEX
    ) as number;

    if (savedTab && this.tabs.find(t => t.key == savedTab)) {
      this.selectedTab = savedTab;
    } else {
      this.selectedTab = TrainingOverviewTab.Current;
    }

    const tabId = this.selectedTab;
    const tabDataMap: Record<TrainingOverviewTab, Observable<any>> = {
      [TrainingOverviewTab.Current]: this.trainingService.getOpenTrainingsForManagement(),
      [TrainingOverviewTab.ToBeClosed]: this.trainingService.getToBeClosedTrainingsForManagement(),
      [TrainingOverviewTab.Closed]: this.trainingService.getCompletedTrainingsForManagement(this.yearFilter!),
      [TrainingOverviewTab.Templates]: this.trainingTemplateService.getAllTrainingTemplates()
    };

    const tabData$ = forkJoin({
      counts: this.trainingService.getTrainingCountsForManagement(this.yearFilter!),
      data: tabDataMap[tabId] as Observable<any[]>
    });

    this.loadingService.load(tabData$).subscribe(res => {
    this.updateTabCounts(res.counts);

    const tabObj = this.tabs.find(t => t.key === this.selectedTab);
    if (tabObj) tabObj.loaded = true;

    switch (tabId) {
      case TrainingOverviewTab.Current:
        this.currentTrainings = res.data.sort((a, b) => this.compareTrainingFirstStartDates(a, b, true));
        break;
      case TrainingOverviewTab.ToBeClosed:
        this.toBeClosedTrainings = res.data.sort((a, b) => this.compareTrainingFirstStartDates(a, b, false));
        break;
      case TrainingOverviewTab.Closed:
        this.closedTrainings = res.data.sort((a, b) => this.compareTrainingFirstStartDates(a, b, false));
        break;
      case TrainingOverviewTab.Templates:
        this.trainingTemplates = res.data;
        break;
      }

      this.setTrainingsForSelectedTab();
      this.dataLoaded = true;
    });
  }

  private configureTabsByRole(): void {
    if (this.currentUserRole === 'Trainer') {
      this.tabs = this.tabs.filter(t => t.key !== TrainingOverviewTab.ToBeClosed);
    }
  }

  private updateTabCounts(counts: any) {
    const tabMap: Record<TrainingOverviewTab, number> = {
      [TrainingOverviewTab.Current]: counts.current,
      [TrainingOverviewTab.ToBeClosed]: counts.toBeClosed,
      [TrainingOverviewTab.Closed]: counts.closed,
      [TrainingOverviewTab.Templates]: counts.templates
    };

    this.tabs.forEach(tab => {
      tab.text = `${tab.textOrig} (${tabMap[tab.key] ?? 0})`;
    });
  }

  private loadTabData(tab: TrainingOverviewTab): void {
    this.trainings = [];
    
    const loaderMap: Record<TrainingOverviewTab, Observable<any[]>> = {
      [TrainingOverviewTab.Current]: this.trainingService.getOpenTrainingsForManagement(),
      [TrainingOverviewTab.ToBeClosed]: this.trainingService.getToBeClosedTrainingsForManagement(),
      [TrainingOverviewTab.Closed]: this.trainingService.getCompletedTrainingsForManagement(this.yearFilter!),
      [TrainingOverviewTab.Templates]: this.trainingTemplateService.getAllTrainingTemplates()
    };

    this.loadingService.load(loaderMap[tab]).subscribe(data => {
      const tabObj = this.tabs.find(t => t.key === tab);
      if (tabObj) {
        tabObj.loaded = true;
        this.updateTabTitle(tab, data.length);
      }

      switch (tab) {
        case TrainingOverviewTab.Current:
          this.currentTrainings = data.sort((a, b) =>
            this.compareTrainingFirstStartDates(a, b, true)
          );
          break;

        case TrainingOverviewTab.ToBeClosed:
          this.toBeClosedTrainings = data.sort((a, b) =>
            this.compareTrainingFirstStartDates(a, b, false)
          );
          break;

        case TrainingOverviewTab.Closed:
          this.closedTrainings = data.sort((a, b) =>
            this.compareTrainingFirstStartDates(a, b, false)
          );
          break;

        case TrainingOverviewTab.Templates:
          this.trainingTemplates = data;
          break;
      }

      this.setTrainingsForSelectedTab();
    });
  }

  reloadClosedTrainings() {
    this.loadingService
      .load(
        this.trainingService.getCompletedTrainingsForManagement(this.yearFilter!)
      )      
      .subscribe((closedTrainings) => {
        this.closedTrainings = closedTrainings.sort((t1, t2) =>
          this.compareTrainingFirstStartDates(t1, t2, false)
        );

        this.updateTabTitle(TrainingOverviewTab.Closed, closedTrainings.length);
        this.setTrainingsForSelectedTab();
      });
  }

  private updateTabTitle(tabKey: TrainingOverviewTab, count: number): void {
    const tabObj = this.tabs.find(t => t.key === tabKey);
    if (tabObj) {
      tabObj.text = `${tabObj.textOrig} (${count})`;
    }
  }

  compareTrainingFirstStartDates(
    t1: TrainingManageSummaryModel,
    t2: TrainingManageSummaryModel,
    asc: boolean
  ): number {
    let d1 = new FirstStartDatePipe().transform(t1.dates);
    const d2 = new FirstStartDatePipe().transform(t2.dates);
    const ts1 = d1 ? d1.valueOf() : Number.MAX_VALUE;
    const ts2 = d2 ? d2.valueOf() : Number.MAX_VALUE;
    return (ts1 - ts2) * (asc ? 1 : -1);
  }

  onSelectedTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedTab = e.itemData.key;

    this.storageService.set(
      StorageKey.TRAINING_OVERVIEW_TAB_INDEX,
      this.selectedTab + ''
    );

    const tabObj = this.tabs.find(t => t.key === this.selectedTab);

    if (tabObj && !tabObj.loaded) {
      this.loadTabData(this.selectedTab);
    } else {
      this.setTrainingsForSelectedTab();
    }
  }


  setTrainingsForSelectedTab() {
    switch (this.selectedTab) {
      case TrainingOverviewTab.Current:
        this.trainings = this.currentTrainings;
        break;

      case TrainingOverviewTab.ToBeClosed:
        this.trainings = this.toBeClosedTrainings;
        break;

      case TrainingOverviewTab.Closed:
        this.trainings = this.closedTrainings;
        break;

      case TrainingOverviewTab.Templates:
        this.trainings = this.trainingTemplates;
        break;
    }
  }

  trainingNavigateHandler(id: string) {
    this.router.navigate(['training', id]);
  }

  trainingNavigateHandlerNewTab(id: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['training', id]));
    window.open(url, '_blank');
  }

  trainingTemplateNavigateHandler(id: string) {
    this.router.navigate(['training-template', id]);
  }

  trainingTemplateNavigateHandlerNewTab(id: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['training-template', id]));
    window.open(url, '_blank');
  }

  onYearFilterChanged(event: any) {
    this.yearFilter = event.value;
    this.savePageState();
    this.reloadClosedTrainings();
  }

  onAddTrainingClick(): void {
    this.router.navigate(['training/add']);
  }

  onAddTrainingTemplateClick(): void {
    this.router.navigate(['training-template/add']);
  }

   private savePageState(): void {
    this.pageStateService.save(this.trainingPageStateKey, {
      yearFilter: this.yearFilter,
    });
  }
  
  private loadPageState(): void {
    const state = this.pageStateService.load<{
      yearFilter: number | null;
    }>(this.trainingPageStateKey);

    if (!state) return;
    this.yearFilter = state.yearFilter;
  }
}
